import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Available themes in the design system
 * - light: Clean professional theme (default)
 * - dark: High contrast for low-light environments
 * - sakura: Warm pink accent theme
 * - andes: Deep teal nature-inspired theme
 */
export type Theme = "light" | "dark" | "sakura" | "andes";

const THEMES: Theme[] = ["light", "dark", "sakura", "andes"];
const THEME_STORAGE_KEY = "zh-corp-tax-theme";
const THEME_CLASS_PREFIX = "theme-";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

/**
 * Apply theme class to document root
 * Handles both Tailwind's .dark class and our .theme-* classes
 */
function applyThemeClass(theme: Theme) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // Remove all theme classes
  THEMES.forEach(t => {
    root.classList.remove(`${THEME_CLASS_PREFIX}${t}`);
  });
  root.classList.remove("dark");

  // Apply dark class for Tailwind dark mode
  if (theme === "dark") {
    root.classList.add("dark");
  }

  // Apply theme class for our design tokens
  root.classList.add(`${THEME_CLASS_PREFIX}${theme}`);
}

/**
 * Get initial theme from localStorage or system preference
 */
function getInitialTheme(defaultTheme: Theme): Theme {
  if (typeof window === "undefined") return defaultTheme;

  // Check localStorage first
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && THEMES.includes(stored as Theme)) {
    return stored as Theme;
  }

  // Fall back to system preference for dark mode
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return defaultTheme;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() =>
    getInitialTheme(defaultTheme)
  );

  // Apply theme on mount and changes
  useEffect(() => {
    applyThemeClass(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly chosen a theme
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setThemeState(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const setTheme = (newTheme: Theme) => {
    if (THEMES.includes(newTheme)) {
      setThemeState(newTheme);
    }
  };

  const toggleTheme = () => {
    // Cycle through themes: light -> dark -> sakura -> andes -> light
    const currentIndex = THEMES.indexOf(theme);
    const nextIndex = (currentIndex + 1) % THEMES.length;
    setThemeState(THEMES[nextIndex]);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        toggleTheme,
        availableThemes: THEMES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
